"use client";

import React from "react";
import styles from "./VideoSection.module.css";
import { CountUpGroup } from "./components/CountUpGroup";
import { DecoImage } from "./components/DecoImage";
import { useResponsive } from "@/hooks/useResponsive";
import { useParams } from "next/navigation";
import ActionButton from "@/components/Common/ActionButton";
import { dictionaries, Locale } from "@/utils/i18n";

interface VideoSectionProps {
  isActive?: boolean;

  stat?: any[];
  propLinkVideo?: string;
  decoImage1?: string;
  decoImage2?: string;
  locale?: string;
}

export default function VideoSection({
  isActive = false,
  stat,
  propLinkVideo,
  decoImage1: propDecoImage1,
  decoImage2: propDecoImage2,
  locale: propLocale,
}: VideoSectionProps) {
  const params = useParams();
  const locale = (propLocale || (params?.locale as Locale) || "vi") as Locale;
  const dict = dictionaries[locale] || dictionaries.vi;
  const buttonLabel =
    dict.homeAbout?.seeMore ||
    (locale === "vi" ? "Giới thiệu về Việt Thiên" : "About Viet Thien");

  const infoList = stat || [];

  // Lấy link video và thêm tham số autoplay, tắt tiếng (mute=1) để chạy mượt và ẩn nút
  let linkVideo = propLinkVideo;
  if (linkVideo && !linkVideo.includes("autoplay=1")) {
    linkVideo +=
      (linkVideo.includes("?") ? "&" : "?") +
      "autoplay=1&mute=0&controls=0&rel=0&modestbranding=1&iv_load_policy=3";
  }

  // Ảnh trang trí từ props hoặc fallback từ videoData
  const decoImage1 = propDecoImage1 || "";
  const decoImage2 = propDecoImage2 || "";

  const { isDesktop, isTablet, isMobile } = useResponsive();
  const [isVideoLoaded, setIsVideoLoaded] = React.useState(false);

  React.useEffect(() => {
    // Đợi 2 giây để Youtube ẩn hết các nút UI rồi mới hiện video
    const timer = setTimeout(() => {
      setIsVideoLoaded(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={styles.video_root}>
      {/* Info Section - Hiển thị bên trên ở Mobile */}
      {isMobile && (
        <CountUpGroup
          infoList={infoList}
          isActive={isActive}
          isDesktop={isDesktop}
          isTablet={isTablet}
          style={{ marginBottom: "30px" }}
        />
      )}

      {/* Video Wrapper */}
      <div className={styles.video_wrapper}>
        {/* Deco left & right - Chỉ hiện ở Desktop/Tablet */}
        {!isMobile && (
          <>
            <DecoImage
              side="left"
              isActive={isActive}
              isDesktop={isDesktop}
              img_url={decoImage1}
            />
            <DecoImage
              side="right"
              isActive={isActive}
              isDesktop={isDesktop}
              img_url={decoImage2}
            />
          </>
        )}

        {/* Video Frame */}
        <div className={styles.video_container}>
          <iframe
            className={styles.video_iframe}
            style={{
              opacity: isVideoLoaded ? 1 : 0,
              transition: "opacity 1s ease-in-out",
            }}
            src={linkVideo}
            title="Viet Thien Video Player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
          {/* Lớp phủ chặn tương tác để ẩn hoàn toàn nút Youtube */}
          <div className={styles.video_overlay}></div>
        </div>

        {/* Nút đặt ngay giữa và nằm trên viền dưới của video */}
        <div className={styles.btn_wrapper}>
          <ActionButton label={buttonLabel} link={`/${locale}/gioi-thieu`} />
        </div>
      </div>

      {/* Info Section - Hiển thị bên dưới ở Desktop/Tablet */}
      {!isMobile && (
        <CountUpGroup
          infoList={infoList}
          isActive={isActive}
          isDesktop={isDesktop}
          isTablet={isTablet}
        />
      )}
    </div>
  );
}
